import React from "react";
import { Helmet } from "react-helmet-async";

const About = () => {
  return (
    <div className='mt-14 bg-white flex items-center justify-center mb-10'>
      <Helmet>
        <title>About Us | tastebud.ee — QR Code Allergen Menus for Street Food</title>
        <meta name='description' content='tastebud.ee was created by someone with food intolerances who knows how hard it is to find safe food at markets. Discover who we are and why we built it.' />
        <link rel='canonical' href='https://tastebud.ee/about' />
      </Helmet>
      <div className='px-6 max-w-screen-sm'>
        <h1 className='text-xl font-bold mb-6'>About tastebud.ee</h1>

        <p className='mb-4'>
          As someone who suffers from multiple food intolerances, and has a genuine love of fresh street and market food, finding something safe to eat at a busy market is rarely straightforward. There is always a worry about wasting a vendor's time with a long list of questions. Sometimes the vendor isn't sure themselves. And at the busiest periods — a Saturday lunch queue stretching back 20 people — asking those questions feels uncomfortable for everyone involved.
        </p>

        <p className='mb-4'>
          tastebud.ee was built to solve that problem from both sides. Customers get a clear, self-service way to check what is in every dish before they even join the queue. Vendors get a way to provide accurate, up-to-date allergen information without interrupting their service — and to demonstrate to a growing group of customers that their stall is a safe and welcoming place to buy from.
        </p>

        <h2 className='text-lg font-semibold mb-3 mt-8'>Why this matters</h2>

        <p className='mb-4'>
          Estimates from the Food Standards Agency and NHS suggest that between 10% and 20% of the UK population manages some form of food allergy, intolerance or medically-driven dietary restriction. At a busy market with 200 visitors on a Saturday, that is up to 40 people who are actively asking themselves whether your stall is safe for them. For most street food vendors, those customers are currently invisible — not because they don't want to buy, but because finding the information they need is too difficult.
        </p>

        <p className='mb-4'>
          UK food law requires allergen information to be available for all food sold to the public, including non-prepacked food at market stalls and street food events. Since{" "}
          <a href='https://www.food.gov.uk/natashas-law' target='_blank' rel='noreferrer' className='underline underline-offset-4'>
            Natasha's Law
          </a>{" "}
          came into force in October 2021, the expectations on food businesses have only increased. tastebud.ee makes compliance straightforward and gives customers the confidence to choose.
        </p>

        <h2 className='text-lg font-semibold mb-3 mt-8'>The content on this site</h2>

        <p className='mb-4'>
          The guides and articles on this site are written to be practical and honest. Where they reference legislation they link to the official source on{" "}
          <a href='https://www.legislation.gov.uk' target='_blank' rel='noreferrer' className='underline underline-offset-4'>
            legislation.gov.uk
          </a>{" "}
          or the{" "}
          <a href='https://www.food.gov.uk' target='_blank' rel='noreferrer' className='underline underline-offset-4'>
            Food Standards Agency
          </a>
          . Where they cite statistics about allergy and intolerance prevalence, the figures come from recognised health organisations. The goal is to give market stall holders genuinely useful information, not to oversimplify complex topics.
        </p>

        <p className='mb-4'>
          If you have questions, feedback, or want to share how you use tastebud.ee, we would love to hear from you at{" "}
          <a href='mailto:hello@tastebud.ee' className='underline underline-offset-4'>
            hello@tastebud.ee
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default About;
